import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useT } from '../../i18n';
import { prefersReducedMotion } from './data';

/**
 * The opening intro (reference home.js): on a deep-teal stage the six
 * curtain folds drop and the wordmark rises while a counter runs to 100%.
 * Then the curtain mark settles over the hero's arch, the opening between
 * the curtains lights up, and the camera flies through it into the hero.
 *
 * Shown at most once per browser session, never with reduced motion, and
 * skippable at any moment (click, Escape/Enter/Space, or Skip). A failsafe
 * hands the page over after a few seconds whatever happens.
 */

const SEEN_KEY = 'aghosha-intro-seen';
const FAILSAFE_MS = 8000;

/** Whether this visit should play the intro (pure: safe to call twice). */
export function shouldPlayIntro(): boolean {
  if (typeof window === 'undefined' || prefersReducedMotion()) return false;
  try {
    return sessionStorage.getItem(SEEN_KEY) !== '1';
  } catch {
    // Storage refused: don't risk replaying it on every visit.
    return false;
  }
}

function markIntroSeen() {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* nothing to remember it in */
  }
}

// Logo artwork units (1305 x 274); the curtain mark is the first 275 units.
const LW = 1305.3;
const LH = 274.3;
const MARK_X = 137.45;
const PORTAL_Y = 200.8;
const PORTAL_R = 73.3;

const HOLE =
  'M136.4 0C90.1 151.6 51.9 213.1 0 274.3L274.9 274.3C223 213.1 184.9 151.6 138.5 0Z';
const MARK = [
  { i: 0, d: 'M0 0L42.7 0C31 21.1 15.9 39.9 0 56Z' },
  { i: 1, d: 'M66.8 0L90.3 0C74.5 58.4 28.5 114.1 0 140.8L0 100.1C34.6 64.3 53.6 29.2 66.8 0Z' },
  { i: 2, d: 'M109.8 0L136.4 0C90.1 151.6 51.9 213.1 0 274.3L0 195.4C74.7 123.7 104.3 21.3 109.8 0Z' },
  { i: 2, d: 'M138.5 0L165.1 0C170.6 21.3 200.2 123.7 274.9 195.4L274.9 274.3C223 213.1 184.9 151.6 138.5 0Z' },
  { i: 1, d: 'M184.6 0L208.1 0C221.3 29.2 240.3 64.3 274.9 100.1L274.9 140.8C246.4 114.1 200.4 58.4 184.6 0Z' },
  { i: 0, d: 'M232.2 0L274.9 0L274.9 56C259 39.9 243.9 21.1 232.2 0' },
];
const A =
  'M67 2.4C84.7 2.4 98.2 -4.2 107.2 -15.3L107.9 0L136.4 0L136.4 -119L105.8 -119L105.8 -105.5C97 -115.7 84 -121.8 67 -121.8C34.9 -121.8 8.3 -94.2 8.3 -59.7C8.3 -25 34.9 2.4 67 2.4ZM72.9 -25.5C53.8 -25.5 39 -40.8 39 -59.7C39 -78.6 53.8 -93.7 72.9 -93.7C91.8 -93.7 106.7 -78.6 106.7 -59.7C106.7 -40.8 91.8 -25.5 72.9 -25.5Z';
const H =
  'M15.8 0L46.3 0L46.3 -73.4C46.5 -85 56.2 -94 69.2 -94C81 -94 90.2 -85.2 90.2 -73.6L90.2 0L120.9 0L120.9 -77C120.9 -101.7 102.2 -121.3 77.4 -121.3C64.9 -121.3 54.3 -116.1 46.3 -108.1L46.3 -181.8L15.8 -181.8Z';
const WORD = [
  { x: 351, d: A },
  {
    x: 503.7,
    d: 'M71.8 56.7C109 56.7 137.1 31.4 137.1 -7.1L137.1 -119L106.2 -119L106.2 -105.3C97.7 -115.2 85.2 -121.3 68.5 -121.3C34.7 -121.3 8.3 -94 8.3 -59.5C8.3 -25.2 34.5 1.2 69.2 1.2C85.5 1.2 97.7 -4.5 106.2 -14.2L106.2 -7.1C106.2 14.9 90.9 29.3 70.8 29.3C56.2 29.3 41.8 21.7 32.8 8L13.5 27.4C26.9 47 50.5 56.7 71.8 56.7ZM72.9 -25.2C53.8 -25.2 39 -40.6 39 -59.5C39 -78.6 53.8 -93.7 72.9 -93.7C91.8 -93.7 107.2 -78.6 107.2 -59.5C107.2 -40.6 91.8 -25.2 72.9 -25.2Z',
  },
  { x: 657.1, d: H },
  {
    x: 790.7,
    d: 'M71.3 2.4C106.7 2.4 134.1 -25 134.1 -59.5C134.1 -94 106.7 -121.3 71.3 -121.3C35.4 -121.3 8.3 -94 8.3 -59.5C8.3 -25 35.4 2.4 71.3 2.4ZM71.3 -26C52.4 -26 37.5 -40.8 37.5 -59.5C37.5 -78.1 52.4 -92.8 71.3 -92.8C89.5 -92.8 104.3 -78.1 104.3 -59.5C104.3 -40.8 89.5 -26 71.3 -26Z',
  },
  {
    x: 930.7,
    d: 'M53.3 2.4C79.3 2.4 98 -10.9 98 -34.9C98 -58.8 80 -66.8 62.3 -72.7C48.4 -77.2 39.9 -79.5 39.9 -87.6C39.9 -93.7 44.8 -97.7 52.4 -97.7C60.4 -97.7 69.4 -93 76.2 -86.2L94.2 -104.3C85 -114.5 70.6 -121.3 53.3 -121.3C30.5 -121.3 9.7 -109 9.7 -85C9.7 -64.2 25.2 -55.9 43.7 -49.3C57.8 -44.4 67.5 -42.2 67.5 -34C67.5 -27.6 61.8 -22.7 52.4 -22.7C43.2 -22.7 32.1 -27.9 23.8 -36.3L6.6 -15.3C16 -5.9 32.8 2.4 53.3 2.4Z',
  },
  { x: 1035.3, d: H },
  { x: 1168.9, d: A },
];

const inOut = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const inOutQuart = (x: number) => (x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2);

interface Props {
  /** The hero takes the stage behind the curtain. */
  onReveal: () => void;
  /** The intro is gone; the page is the user's. */
  onDone: () => void;
}

export function Intro({ onReveal, onDone }: Props) {
  const { t } = useT();
  const root = useRef<HTMLDivElement>(null);
  const cam = useRef<SVGGElement>(null);
  const countEl = useRef<HTMLElement>(null);
  const barEl = useRef<HTMLSpanElement>(null);
  const [phase, setPhase] = useState<'on' | 'opening' | 'flying'>('on');
  const finishRef = useRef<() => void>(() => {});

  // Lock the page and hide the header before the first paint.
  useLayoutEffect(() => {
    const doc = document.documentElement;
    markIntroSeen();
    doc.classList.add('is-intro');
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    return () => doc.classList.remove('is-intro', 'is-landing');
  }, []);

  useEffect(() => {
    const doc = document.documentElement;
    const g = cam.current;
    if (!g) return;
    let finished = false;
    let opening = false;
    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => timers.push(window.setTimeout(fn, ms));

    const view = { cx: LW / 2, cy: LH / 2, s: 1, ay: null as number | null };
    const restScale = () => Math.min(window.innerWidth * 0.74, 620) / LW;
    const setCam = () => {
      const W = window.innerWidth;
      const Hh = window.innerHeight;
      const tx = W / 2 - view.s * view.cx;
      const ty = (view.ay == null ? Hh / 2 : view.ay) - view.s * view.cy;
      g.setAttribute('transform', `translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${view.s.toFixed(5)})`);
    };
    view.s = restScale();
    setCam();
    const onResize = () => {
      if (!opening && !finished) {
        view.s = restScale();
        setCam();
      }
    };
    window.addEventListener('resize', onResize);

    const finish = () => {
      if (finished) return;
      finished = true;
      timers.forEach(clearTimeout);
      doc.classList.remove('is-intro', 'is-landing');
      onReveal();
      onDone();
      const logo = document.querySelector('.site-header .logo');
      if (logo) {
        logo.classList.add('is-arriving');
        window.setTimeout(() => logo.classList.remove('is-arriving'), 2200);
      }
    };
    finishRef.current = () => {
      if (!opening) finish();
    };
    later(finish, FAILSAFE_MS);

    const tween = (ms: number, step: (e: number) => void, ease: (x: number) => number) =>
      new Promise<void>((resolve) => {
        const start = performance.now();
        const frame = (now: number) => {
          if (finished) return resolve();
          const p = Math.min(1, (now - start) / ms);
          step(ease(p));
          if (p < 1) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });

    const open = () => {
      if (finished || opening) return;
      opening = true;
      const W = window.innerWidth;
      const Hh = window.innerHeight;
      setPhase('opening');
      later(onReveal, 250);

      // Land the curtain mark over the hero arch, so the photo shows through it.
      const hero = document.querySelector<HTMLElement>('[data-hero]');
      const pin = hero?.querySelector<HTMLElement>('.hero-pin');
      const cs = hero ? getComputedStyle(hero) : null;
      const archTop = (cs && parseFloat(cs.getPropertyValue('--at'))) || Hh * 0.3;
      const archBot = (pin?.clientHeight ?? Hh) - ((cs && parseFloat(cs.getPropertyValue('--ab'))) || Hh * 0.3);
      const archY = (archTop + archBot) / 2;
      const archH = Math.max(80, archBot - archTop);
      const s0 = view.s;
      const cx0 = view.cx;
      const cy0 = view.cy;
      const s1 = Math.min(Math.min(W, Hh) * 0.36, archH * 0.78) / LH;
      tween(
        1000,
        (e) => {
          view.s = s0 + (s1 - s0) * e;
          view.ay = Hh / 2 + (archY - Hh / 2) * e;
          view.cx = cx0 + (MARK_X - cx0) * e;
          view.cy = cy0 + (LH / 2 - cy0) * e;
          setCam();
        },
        inOut,
      )
        .then(() => new Promise<void>((r) => later(r, 380)))
        .then(() => {
          if (finished) return;
          // Fly through the opening into the hero.
          const s2 = Math.hypot(W / 2, Math.max(archY, Hh - archY)) / (PORTAL_R * 0.86);
          const cyA = view.cy;
          setPhase('flying');
          doc.classList.add('is-landing');
          return tween(
            1500,
            (e) => {
              view.s = s1 * Math.pow(s2 / s1, e);
              view.cx = MARK_X;
              view.cy = cyA + (PORTAL_Y - cyA) * Math.min(1, e * 1.4);
              setCam();
            },
            inOutQuart,
          );
        })
        .then(finish);
    };

    // Progress: real loading (first hero photo + fonts), never faster than MIN.
    const MIN = 2400;
    const CAP = 4500;
    const t0 = performance.now();
    let loaded = false;
    let shown = 0;
    const waits: Promise<unknown>[] = [];
    const first = document.querySelector<HTMLImageElement>('[data-hero] .slide.is-active');
    if (first?.decode) waits.push(first.decode().catch(() => {}));
    if (document.fonts?.ready) waits.push(document.fonts.ready.catch(() => {}));
    Promise.all(waits).then(() => {
      loaded = true;
    });
    later(() => {
      loaded = true;
    }, CAP);

    let raf = 0;
    const tick = (now: number) => {
      if (finished) return;
      const tp = Math.min(1, (now - t0) / MIN);
      const eased = 1 - Math.pow(1 - tp, 2.2);
      const target = loaded ? eased : Math.min(eased, 0.9);
      shown += (target - shown) * 0.18;
      if (target - shown < 0.004) shown = target;
      if (countEl.current) countEl.current.textContent = String(Math.round(shown * 100));
      barEl.current?.style.setProperty('--p', shown.toFixed(3));
      if (shown >= 1 && loaded) {
        if (countEl.current) countEl.current.textContent = '100';
        later(open, 350);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        finishRef.current();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      finished = true;
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
    };
    // Runs once per mount; the callbacks are stable for the intro's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cls = ['intro', 'is-on', phase !== 'on' && 'is-counted is-opening', phase === 'flying' && 'is-flying']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls} ref={root} aria-hidden="true" onClick={() => finishRef.current()}>
      <svg className="intro-stage" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="ic-gold" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="275" y2="275">
            <stop offset="0" stopColor="#f3d993" />
            <stop offset=".5" stopColor="#d4a653" />
            <stop offset="1" stopColor="#b5843a" />
          </linearGradient>
          <clipPath id="ic-wclip">
            <rect x="320" y="-80" width="1040" height="338" />
          </clipPath>
        </defs>
        <g className="ic-cam" ref={cam}>
          <path className="ic-stage" fillRule="evenodd" d={`M-20000 -20000H20000V20000H-20000Z${HOLE}`} />
          <path className="ic-hole" d={HOLE} />
          <path className="ic-rim" d={HOLE} fill="none" stroke="url(#ic-gold)" vectorEffect="non-scaling-stroke" />
          <g className="ic-mark" fill="url(#ic-gold)">
            {MARK.map((p) => (
              <path key={p.d} style={{ '--i': p.i } as CSSProperties} d={p.d} />
            ))}
          </g>
          <g className="ic-word" clipPath="url(#ic-wclip)">
            {WORD.map((w, i) => (
              <g key={w.x} transform={`translate(${w.x} 199.7)`}>
                <path style={{ '--i': i } as CSSProperties} d={w.d} />
              </g>
            ))}
          </g>
        </g>
      </svg>
      <div className="intro-foot">
        <span className="intro-tag">{t('hmIntroTag')}</span>
        <span className="intro-count">
          <b ref={countEl}>0</b>%
        </span>
        <span className="intro-bar" ref={barEl}>
          <i />
        </span>
      </div>
      <button type="button" className="intro-skip" tabIndex={-1} onClick={() => finishRef.current()}>
        {t('hmIntroSkip')}
      </button>
    </div>
  );
}
